import React, { useState, useCallback, useEffect } from 'react';
import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  Box,
  Button,
  VStack,
  HStack,
  Spinner,
  Icon,
  Avatar,
  Badge,
  useToast,
  Divider,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import {
  Bell,
  CheckCircle,
  Check,
  Copy,
  Key,
  XCircle,
  UserPlus,
  WhatsappLogo,
} from 'phosphor-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { toast as sonnerToast } from 'sonner';
import { useNotificationContext } from '../../providers/notification';
import { useNotificacoes, INotificacao } from '../../hooks/useNotificacoes';
import { getNotificationConfig } from '../../constants/notificationTypes';
import { useSolicitacaoAcesso, ISolicitacaoAcesso } from '../../hooks/useSolicitacaoAcesso';
import { useSolicitacaoTokens } from '../../hooks/useSolicitacaoTokens';
import { useAuth } from '../../providers/auth';
import { IDataNoPagination } from '../../types/shared';
import { resolverDestinoDeNotificacao } from '../../constants/notificationHandlers';
import {
  useNotificationStore,
  type ChatNotification,
} from '../../stores/whatsapp/notificationStore';

const GRUPO_ADMIN_ID = 1;

const formatarDuracao = (horas: number | undefined | null): string => {
  if (!horas) return '7 dias';
  if (horas < 24) {
    return horas === 1 ? '1 hora' : `${horas} horas`;
  }
  const dias = Math.floor(horas / 24);
  if (dias === 1) return '1 dia';
  if (dias === 7) return '7 dias';
  return `${dias} dias`;
};

// ─── NotificationItem (existente) ───────────────────────────────
interface NotificationItemProps {
  notificacao: INotificacao;
  // eslint-disable-next-line no-unused-vars
  onMarkRead: (id: number) => void;
  // eslint-disable-next-line no-unused-vars
  onNavigate: (n: INotificacao) => void;
}

function NotificationItem({ notificacao, onMarkRead, onNavigate }: NotificationItemProps) {
  const config = getNotificationConfig(notificacao.tipo);
  const isUnread = !notificacao.dt_leitura;

  return (
    <Box
      p={3}
      bg={isUnread ? 'blue.50' : 'white'}
      borderRadius="md"
      border="1px solid"
      borderColor={isUnread ? 'blue.200' : 'gray.100'}
      cursor="pointer"
      _hover={{ bg: isUnread ? 'blue.100' : 'gray.50' }}
      transition="all 0.15s"
      onClick={() => onNavigate(notificacao)}
    >
      <HStack spacing={3} align="flex-start">
        <Box mt="2px">
          <Icon as={config.icon} boxSize={5} color={config.color} />
        </Box>
        <Box flex={1} minW={0}>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="sm" fontWeight={isUnread ? 'bold' : 'medium'} noOfLines={1}>
              {notificacao.titulo}
            </Text>
            {isUnread && <Box w="8px" h="8px" bg="blue.500" borderRadius="full" flexShrink={0} />}
          </HStack>
          <Text fontSize="xs" color="gray.600" noOfLines={2}>
            {notificacao.mensagem}
          </Text>
          <HStack mt={1} spacing={2}>
            <Text fontSize="xs" color="gray.400">
              {moment(notificacao.dt_criacao).fromNow()}
            </Text>
            <Text fontSize="xs" color={config.color} fontWeight="medium">
              {config.label}
            </Text>
          </HStack>
        </Box>
      </HStack>
      {isUnread && (
        <Flex justify="flex-end" mt={1}>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            onClick={e => {
              e.stopPropagation();
              onMarkRead(notificacao.id);
            }}
          >
            Marcar como lida
          </Button>
        </Flex>
      )}
    </Box>
  );
}

// ─── SolicitacaoRecebidaItem ────────────────────────────────────
interface SolicitacaoRecebidaItemProps {
  solicitacao: ISolicitacaoAcesso;
}

function SolicitacaoRecebidaItem({ solicitacao }: SolicitacaoRecebidaItemProps) {
  const chakraToast = useToast();
  const queryClient = useQueryClient();
  const { aprovarSolicitacao, rejeitarSolicitacao } = useSolicitacaoAcesso();
  const { saveToken } = useSolicitacaoTokens();

  const { mutateAsync: aprovar, isPending: isAprovando } = useMutation({
    mutationFn: (id: number) => aprovarSolicitacao(id),
    onSuccess: (response, solicitacaoId) => {
      const sol = response?.data;
      const idResp = sol?.id || solicitacaoId;
      const token = sol?.token_acesso;

      if (token && idResp) {
        saveToken(idResp, token);
        window.dispatchEvent(
          new CustomEvent('token-recebido', {
            detail: { solicitacao_id: idResp, token },
          }),
        );
      }
      chakraToast({
        title: 'Acesso aprovado!',
        description: 'O token foi gerado e enviado ao administrador.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      queryClient.invalidateQueries({ queryKey: ['solicitacoes-recebidas'] });
      queryClient.invalidateQueries({ queryKey: ['solicitacoes-pendentes'] });
      queryClient.invalidateQueries({ queryKey: ['minhas-solicitacoes'] });
    },
    onError: () => {
      chakraToast({
        title: 'Erro ao aprovar',
        description: 'Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const { mutateAsync: rejeitar, isPending: isRejeitando } = useMutation({
    mutationFn: (id: number) => rejeitarSolicitacao(id),
    onSuccess: () => {
      chakraToast({
        title: 'Solicitação rejeitada',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      queryClient.invalidateQueries({ queryKey: ['solicitacoes-recebidas'] });
      queryClient.invalidateQueries({ queryKey: ['solicitacoes-pendentes'] });
      queryClient.invalidateQueries({ queryKey: ['minhas-solicitacoes'] });
    },
    onError: () => {
      chakraToast({
        title: 'Erro ao rejeitar',
        description: 'Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const statusConfig: Record<
    string,
    { bg: string; borderColor: string; badge: string; badgeColor: string }
  > = {
    pendente: {
      bg: 'purple.50',
      borderColor: 'purple.200',
      badge: 'Pendente',
      badgeColor: 'yellow',
    },
    aprovado: { bg: 'green.50', borderColor: 'green.200', badge: 'Aprovado', badgeColor: 'green' },
    rejeitado: { bg: 'red.50', borderColor: 'red.200', badge: 'Rejeitado', badgeColor: 'red' },
  };
  const cfg = statusConfig[solicitacao.status] || statusConfig.pendente;

  return (
    <Box
      p={3}
      borderWidth={1}
      borderRadius="md"
      borderColor={cfg.borderColor}
      bg={cfg.bg}
      opacity={solicitacao.status !== 'pendente' ? 0.85 : 1}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <Flex align="center" gap={2}>
          <Avatar
            size="sm"
            name={solicitacao.solicitante?.nome}
            src={solicitacao.solicitante?.imagem_url}
          />
          <Box>
            <Text fontSize="sm" fontWeight="bold">
              {solicitacao.solicitante?.nome}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {solicitacao.solicitante?.email}
            </Text>
          </Box>
        </Flex>
        <Badge colorScheme={cfg.badgeColor} fontSize="xs">
          {cfg.badge}
        </Badge>
      </Flex>

      {solicitacao.status === 'pendente' && (
        <>
          <Text fontSize="xs" color="gray.600" mb={2}>
            Solicitou acesso à sua conta por{' '}
            <strong>{formatarDuracao(solicitacao.duracao_horas)}</strong>.
          </Text>
          <Text fontSize="xs" color="gray.400" mb={2}>
            {moment(solicitacao.dt_criacao).fromNow()}
          </Text>
          <HStack spacing={2}>
            <Button
              colorScheme="green"
              size="xs"
              leftIcon={<Check size={14} />}
              onClick={() => aprovar(solicitacao.id)}
              isLoading={isAprovando}
              flex={1}
            >
              Permitir
            </Button>
            <Button
              colorScheme="red"
              variant="outline"
              size="xs"
              leftIcon={<XCircle size={14} />}
              onClick={() => rejeitar(solicitacao.id)}
              isLoading={isRejeitando}
              flex={1}
            >
              Rejeitar
            </Button>
          </HStack>
        </>
      )}

      {solicitacao.status === 'aprovado' && (
        <Flex align="center" gap={2}>
          <Check size={14} color="#38A169" />
          <Text fontSize="xs" color="green.600">
            Token gerado e enviado ao administrador.
          </Text>
        </Flex>
      )}

      {solicitacao.status === 'rejeitado' && (
        <Flex align="center" gap={2}>
          <XCircle size={14} color="#E53E3E" />
          <Text fontSize="xs" color="red.600">
            Solicitação rejeitada.
          </Text>
        </Flex>
      )}
    </Box>
  );
}

// ─── TokenAprovadoItem ──────────────────────────────────────────
interface TokenAprovadoItemProps {
  solicitacao: ISolicitacaoAcesso;
  isVista: boolean;
  // eslint-disable-next-line no-unused-vars
  onMarcarVista: (id: number) => void;
}

function TokenAprovadoItem({ solicitacao, isVista, onMarcarVista }: TokenAprovadoItemProps) {
  const chakraToast = useToast();
  const { getToken } = useSolicitacaoTokens();

  const tokenDaApi = solicitacao.token_acesso;
  const tokenLocal = solicitacao.id ? getToken(solicitacao.id) : undefined;
  const token = tokenDaApi || tokenLocal;

  const copyToClipboard = (t: string) => {
    navigator.clipboard.writeText(t);
    chakraToast({
      title: 'Token copiado!',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (isVista) {
    return (
      <Box
        p={3}
        borderWidth={1}
        borderRadius="md"
        borderColor="gray.200"
        bg="gray.50"
        opacity={0.7}
      >
        <Flex justify="space-between" align="center" mb={2}>
          <Flex align="center" gap={2}>
            <Avatar
              size="sm"
              name={solicitacao.usuario_alvo?.nome}
              src={solicitacao.usuario_alvo?.imagem_url}
            />
            <Box>
              <Text fontSize="sm" fontWeight="bold" color="gray.500">
                {solicitacao.usuario_alvo?.nome}
              </Text>
              <Text fontSize="xs" color="gray.400">
                Aprovou sua solicitação de acesso
              </Text>
            </Box>
          </Flex>
          <Badge colorScheme="gray" fontSize="xs">
            Visto
          </Badge>
        </Flex>
        <Box bg="white" p={2} borderRadius="md">
          <Text fontSize="xs" color="gray.400">
            Token já visualizado.
          </Text>
          {solicitacao.dt_expiracao && (
            <Text fontSize="xs" color="gray.400" mt={1}>
              Expira em: {new Date(solicitacao.dt_expiracao).toLocaleDateString('pt-BR')}
            </Text>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3} borderWidth={1} borderRadius="md" borderColor="green.200" bg="green.50">
      <Flex justify="space-between" align="center" mb={2}>
        <Flex align="center" gap={2}>
          <Avatar
            size="sm"
            name={solicitacao.usuario_alvo?.nome}
            src={solicitacao.usuario_alvo?.imagem_url}
          />
          <Box>
            <Text fontSize="sm" fontWeight="bold">
              {solicitacao.usuario_alvo?.nome}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Aprovou sua solicitação de acesso
            </Text>
          </Box>
        </Flex>
        <Badge colorScheme="green" fontSize="xs">
          Aprovado
        </Badge>
      </Flex>

      {token ? (
        <Box bg="white" p={2} borderRadius="md" mb={2}>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Token de acesso:
          </Text>
          <Flex
            align="center"
            gap={2}
            bg="gray.100"
            p={2}
            borderRadius="md"
            border="1px solid"
            borderColor="green.300"
          >
            <Text fontFamily="mono" fontWeight="bold" fontSize="sm" flex={1}>
              {token}
            </Text>
            <Button size="xs" variant="ghost" onClick={() => copyToClipboard(token)}>
              <Copy size={14} />
            </Button>
          </Flex>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Expira em:{' '}
            {solicitacao.dt_expiracao
              ? new Date(solicitacao.dt_expiracao).toLocaleDateString('pt-BR')
              : 'N/A'}
          </Text>
        </Box>
      ) : (
        <Box bg="yellow.50" p={2} borderRadius="md" mb={2}>
          <Text fontSize="xs" color="yellow.700">
            Token não disponível. Verifique na página de usuários.
          </Text>
        </Box>
      )}

      <Button
        size="xs"
        colorScheme="gray"
        variant="outline"
        onClick={() => onMarcarVista(solicitacao.id)}
        width="100%"
      >
        Marcar como visto
      </Button>
    </Box>
  );
}

// ─── SectionHeader ──────────────────────────────────────────────
function SectionHeader({
  icon,
  label,
  count,
  color,
}: {
  icon: React.ElementType;
  label: string;
  count?: number;
  color: string;
}) {
  return (
    <Flex align="center" gap={2} mb={2}>
      <Icon as={icon} boxSize={4} color={color} />
      <Text fontSize="sm" fontWeight="bold" color="gray.700">
        {label}
      </Text>
      {count !== undefined && count > 0 && (
        <Badge colorScheme={color.split('.')[0]} fontSize="xs">
          {count}
        </Badge>
      )}
    </Flex>
  );
}

// ─── NotificationPanel (principal) ──────────────────────────────
// ─── WhatsApp Notification Item ──────────────────────────────────
function WaNotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: {
  notification: ChatNotification;
  // eslint-disable-next-line no-unused-vars
  onMarkRead: (id: string) => void;
  // eslint-disable-next-line no-unused-vars
  onNavigate: (conversationId: string) => void;
}) {
  const isUnread = !notification.read;
  const typeLabels: Record<string, { label: string; color: string }> = {
    new_message: { label: 'Nova mensagem', color: 'green' },
    new_conversation: { label: 'Nova conversa', color: 'blue' },
    transfer: { label: 'Transferência', color: 'orange' },
    assignment: { label: 'Atribuição', color: 'purple' },
    system: { label: 'Sistema', color: 'gray' },
  };
  const typeInfo = typeLabels[notification.type] || typeLabels.system;

  return (
    <Box
      p={3}
      bg={isUnread ? 'green.50' : 'white'}
      borderRadius="md"
      border="1px solid"
      borderColor={isUnread ? 'green.200' : 'gray.100'}
      cursor="pointer"
      _hover={{ bg: isUnread ? 'green.100' : 'gray.50' }}
      transition="all 0.15s"
      onClick={() => {
        if (notification.conversation_id) onNavigate(notification.conversation_id);
        if (isUnread) onMarkRead(notification.id);
      }}
    >
      <HStack spacing={3} align="flex-start">
        <Box mt="2px">
          <WhatsappLogo size={20} weight="fill" color="#25D366" />
        </Box>
        <Box flex={1} minW={0}>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="sm" fontWeight={isUnread ? 'bold' : 'medium'} noOfLines={1}>
              {notification.title}
            </Text>
            {isUnread && <Box w="8px" h="8px" bg="green.500" borderRadius="full" flexShrink={0} />}
          </HStack>
          <Text fontSize="xs" color="gray.600" noOfLines={2}>
            {notification.message}
          </Text>
          <HStack mt={1} spacing={2}>
            <Text fontSize="xs" color="gray.400">
              {moment(notification.created_at).fromNow()}
            </Text>
            <Text fontSize="xs" color={`${typeInfo.color}.500`} fontWeight="medium">
              {typeInfo.label}
            </Text>
            {notification.department && (
              <Badge fontSize="9px" colorScheme="gray" variant="subtle">
                {notification.department}
              </Badge>
            )}
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
}

export function NotificationPanel() {
  const { isOpen, closePanel } = useNotificationContext();
  const { getNotificacoes, marcarComoLida, marcarTodasComoLidas } = useNotificacoes();
  const { getRecebidas, getMinhasSolicitacoes } = useSolicitacaoAcesso();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isAdmin = user?.grupo_id === GRUPO_ADMIN_ID;

  // ── Aprovações vistas (localStorage) ──
  const [aprovacoesVistas, setAprovacoesVistas] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem('aprovacoes_vistas');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // ── Queries ──
  const { data, isLoading } = useQuery({
    queryKey: ['notificacoes'],
    queryFn: () => getNotificacoes({ limit: 50 }),
    enabled: isOpen,
  });

  const { data: recebidasData, isLoading: isLoadingRecebidas } = useQuery<
    IDataNoPagination<ISolicitacaoAcesso[]>
  >({
    queryKey: ['solicitacoes-recebidas'],
    queryFn: getRecebidas,
    enabled: isOpen,
  });

  const { data: minhasData, isLoading: isLoadingMinhas } = useQuery<
    IDataNoPagination<ISolicitacaoAcesso[]>
  >({
    queryKey: ['minhas-solicitacoes'],
    queryFn: getMinhasSolicitacoes,
    enabled: isOpen && isAdmin,
  });

  // ── Mutations (notificações) ──
  const { mutate: markRead } = useMutation({
    mutationFn: marcarComoLida,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
      queryClient.invalidateQueries({ queryKey: ['notificacoes-unread-count'] });
    },
    onError: () => {
      sonnerToast.error('Erro ao marcar notificacao como lida');
    },
  });

  const { mutate: markAllRead, isPending: isMarkingAll } = useMutation({
    mutationFn: marcarTodasComoLidas,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
      queryClient.invalidateQueries({ queryKey: ['notificacoes-unread-count'] });
      sonnerToast.success(`${res.data.total_marcadas} notificacao(oes) marcada(s) como lida(s)`);
    },
  });

  // ── Handlers ──
  const handleNavigate = useCallback(
    (notificacao: INotificacao) => {
      if (!notificacao.dt_leitura) {
        markRead(notificacao.id);
      }
      // A rota vem do registry de notificações, declarada pelo domínio dono
      // dela — mesma composição por domínio que os handlers de efeito usam.
      const destino = resolverDestinoDeNotificacao(notificacao);
      if (destino) {
        closePanel();
        navigate(destino);
      }
    },
    [markRead, closePanel, navigate],
  );

  const marcarAprovacaoComoVista = useCallback(
    (id: number) => {
      const novasVistas = new Set(aprovacoesVistas);
      novasVistas.add(id);
      setAprovacoesVistas(novasVistas);
      localStorage.setItem('aprovacoes_vistas', JSON.stringify(Array.from(novasVistas)));
    },
    [aprovacoesVistas],
  );

  // Recarrega tokens do localStorage quando o painel abre
  const { loadTokensFromStorage } = useSolicitacaoTokens();
  useEffect(() => {
    if (isOpen) {
      loadTokensFromStorage();
    }
  }, [isOpen, loadTokensFromStorage]);

  // ── Dados derivados ──
  const notificacoes = data?.data || [];
  const hasUnread = notificacoes.some(n => !n.dt_leitura);

  const solicitacoesRecebidas = recebidasData?.data ?? [];
  const pendentesCount = solicitacoesRecebidas.filter(s => s.status === 'pendente').length;

  const minhasSolicitacoes = minhasData?.data ?? [];
  const aprovacoes = minhasSolicitacoes.filter(s => s.status === 'aprovado');
  const aprovacoesPendentesCount = aprovacoes.filter(s => !aprovacoesVistas.has(s.id)).length;

  const hasSolicitacoes = solicitacoesRecebidas.length > 0;
  const hasAprovacoes = aprovacoes.length > 0;

  // ── WhatsApp notifications ──
  const waNotifications = useNotificationStore(s => s.notifications);
  const waUnreadCount = useNotificationStore(s => s.unreadCount);
  const waMarkAsRead = useNotificationStore(s => s.markAsRead);
  const waMarkAllRead = useNotificationStore(s => s.markAllRead);
  const waClearAll = useNotificationStore(s => s.clearAll);

  const handleWaNavigate = useCallback(
    (conversationId: string) => {
      closePanel();
      navigate(`/whatsapp?conversation=${conversationId}`);
    },
    [closePanel, navigate],
  );

  return (
    <Drawer isOpen={isOpen} onClose={closePanel} size="md" placement="right" preserveScrollBarGap>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="0" pb={0} pt={4} px={5}>
          <Flex align="center" justify="space-between">
            <Text fontSize="xl" fontWeight="bold" color="gray.800">
              Notificações
            </Text>
            <DrawerCloseButton position="static" />
          </Flex>
        </DrawerHeader>

        <Tabs variant="unstyled" colorScheme="blue" size="sm" isLazy>
          <TabList px={5} pt={2} pb={0} gap={2} borderBottom="1px solid" borderColor="gray.100">
            <Tab
              fontSize="sm"
              fontWeight="semibold"
              color="gray.400"
              pb={3}
              px={1}
              borderBottom="2px solid transparent"
              _selected={{ color: 'blue.600', borderColor: 'blue.600' }}
              _hover={{ color: 'gray.600' }}
            >
              <HStack spacing={2}>
                <Bell size={15} weight="bold" />
                <Text>Geral</Text>
                {hasUnread && (
                  <Flex
                    bg="blue.500"
                    color="white"
                    borderRadius="full"
                    minW="20px"
                    h="20px"
                    fontSize="10px"
                    fontWeight="bold"
                    alignItems="center"
                    justifyContent="center"
                    px="6px"
                  >
                    {notificacoes.filter(n => !n.dt_leitura).length}
                  </Flex>
                )}
              </HStack>
            </Tab>
            <Tab
              fontSize="sm"
              fontWeight="semibold"
              color="gray.400"
              pb={3}
              px={1}
              borderBottom="2px solid transparent"
              _selected={{ color: 'green.600', borderColor: 'green.600' }}
              _hover={{ color: 'gray.600' }}
            >
              <HStack spacing={2}>
                <WhatsappLogo size={15} weight="fill" />
                <Text>WhatsApp</Text>
                {waUnreadCount > 0 && (
                  <Flex
                    bg="green.500"
                    color="white"
                    borderRadius="full"
                    minW="20px"
                    h="20px"
                    fontSize="10px"
                    fontWeight="bold"
                    alignItems="center"
                    justifyContent="center"
                    px="6px"
                  >
                    {waUnreadCount}
                  </Flex>
                )}
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* ── Aba Geral ── */}
            <TabPanel p={4}>
              {hasUnread && (
                <Flex justify="flex-end" mb={3}>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => markAllRead()}
                    isLoading={isMarkingAll}
                  >
                    Marcar todas como lidas
                  </Button>
                </Flex>
              )}

              {/* Solicitações de acesso recebidas */}
              {(hasSolicitacoes || isLoadingRecebidas) && (
                <Box mb={4}>
                  <SectionHeader
                    icon={UserPlus}
                    label="Solicitações de Acesso"
                    count={pendentesCount}
                    color="purple.500"
                  />
                  {isLoadingRecebidas ? (
                    <Flex justify="center" py={4}>
                      <Spinner size="sm" color="purple.500" />
                    </Flex>
                  ) : (
                    <VStack spacing={2} align="stretch">
                      {solicitacoesRecebidas.map(s => (
                        <SolicitacaoRecebidaItem key={s.id} solicitacao={s} />
                      ))}
                    </VStack>
                  )}
                  <Divider mt={4} />
                </Box>
              )}

              {/* Tokens aprovados (admin) */}
              {isAdmin && (hasAprovacoes || isLoadingMinhas) && (
                <Box mb={4}>
                  <SectionHeader
                    icon={Key}
                    label="Tokens Aprovados"
                    count={aprovacoesPendentesCount}
                    color="green.500"
                  />
                  {isLoadingMinhas ? (
                    <Flex justify="center" py={4}>
                      <Spinner size="sm" color="green.500" />
                    </Flex>
                  ) : (
                    <VStack spacing={2} align="stretch">
                      {aprovacoes.map(s => (
                        <TokenAprovadoItem
                          key={s.id}
                          solicitacao={s}
                          isVista={aprovacoesVistas.has(s.id)}
                          onMarcarVista={marcarAprovacaoComoVista}
                        />
                      ))}
                    </VStack>
                  )}
                  <Divider mt={4} />
                </Box>
              )}

              {/* Notificações regulares */}
              {(hasSolicitacoes || hasAprovacoes) && notificacoes.length > 0 && (
                <SectionHeader icon={Bell} label="Notificacoes" color="blue.500" />
              )}

              {isLoading && (
                <Flex justify="center" py={10}>
                  <Spinner size="lg" color="blue.500" />
                </Flex>
              )}

              {!isLoading && notificacoes.length === 0 && !hasSolicitacoes && !hasAprovacoes && (
                <Flex direction="column" align="center" justify="center" py={16} color="gray.400">
                  <CheckCircle size={48} />
                  <Text mt={4} fontSize="md">
                    Nenhuma notificacao
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Voce esta em dia!
                  </Text>
                </Flex>
              )}

              {!isLoading && notificacoes.length > 0 && (
                <VStack spacing={2} align="stretch">
                  {notificacoes.map(notificacao => (
                    <NotificationItem
                      key={notificacao.id}
                      notificacao={notificacao}
                      onMarkRead={id => markRead(id)}
                      onNavigate={handleNavigate}
                    />
                  ))}
                </VStack>
              )}
            </TabPanel>

            {/* ── Aba WhatsApp ── */}
            <TabPanel p={4}>
              {waNotifications.length > 0 && (
                <Flex justify="flex-end" mb={3} gap={2}>
                  <Button size="xs" variant="outline" colorScheme="green" onClick={waMarkAllRead}>
                    Marcar todas como lidas
                  </Button>
                  <Button size="xs" variant="outline" colorScheme="red" onClick={waClearAll}>
                    Limpar
                  </Button>
                </Flex>
              )}

              {waNotifications.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={16} color="gray.400">
                  <WhatsappLogo size={48} weight="fill" />
                  <Text mt={4} fontSize="md">
                    Nenhuma notificacao
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Novas mensagens do WhatsApp aparecerão aqui
                  </Text>
                </Flex>
              ) : (
                <VStack spacing={2} align="stretch">
                  {waNotifications.map(n => (
                    <WaNotificationItem
                      key={n.id}
                      notification={n}
                      onMarkRead={waMarkAsRead}
                      onNavigate={handleWaNavigate}
                    />
                  ))}
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </DrawerContent>
    </Drawer>
  );
}
